import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { me as meApi } from '@/api/endpoints'
import { selectedOrganization, selectOrganization } from '@/api/org'
import type { CurrentOrganization } from '@/api/types'
import { getOrganizations, getRoles, getUser, login, logout } from '@/auth/keycloak'

/**
 * The Keycloak roles mdm-service knows (`SecurityRoles`), as the console sees them:
 * `mdm-org-admin` arrives as `ROLE_MDM_ORG_ADMIN` (see `getRoles`).
 *
 * These flags decide what is rendered. They are a convenience, not a control: the
 * server enforces every one of them independently, so a user who reaches an
 * endpoint another way is still refused.
 */
export const Role = {
  PLATFORM_ADMIN: 'ROLE_MDM_PLATFORM_ADMIN',
  /** web-app's MANAGER: any organization, but no platform configuration. */
  PLATFORM_MANAGER: 'ROLE_MDM_PLATFORM_MANAGER',
  ORG_ADMIN: 'ROLE_MDM_ORG_ADMIN',
  OPERATOR: 'ROLE_MDM_OPERATOR',
  AUDITOR: 'ROLE_MDM_AUDITOR',
  HELPDESK: 'ROLE_MDM_HELPDESK',
  /** A distributor's cabinet: its own organizations, without working in them. */
  DISTRIBUTOR: 'ROLE_MDM_DISTRIBUTOR',
} as const

export const useAuthStore = defineStore('auth', () => {
  const username = ref<string | null>(null)
  const displayName = ref<string | null>(null)
  const email = ref<string | null>(null)
  const roles = ref<string[]>([])
  /** Aliases from the token's `organization` claim. */
  const organizations = ref<string[]>([])
  /** What mdm-service resolved this session to; null when it could not. */
  const currentOrganization = ref<CurrentOrganization | null>(null)
  const loading = ref(true)

  const authenticated = computed(() => username.value !== null)

  function hasRole(...wanted: string[]): boolean {
    return wanted.some((role) => roles.value.includes(role))
  }

  /**
   * Whether the token carries any role this console is built around.
   *
   * <p>False is not a permission problem to work around but a Keycloak realm that
   * has granted this person nothing yet — worth naming, because the server's answer
   * to a role-less token is 403 on every screen, which the browser renders as an
   * empty table rather than a refusal.
   */
  const hasAnyPanelRole = computed(() => hasRole(...Object.values(Role)))

  const isPlatformAdmin = computed(() => hasRole(Role.PLATFORM_ADMIN))

  /** Platform admins and managers: may act in any organization (`X-Org-Id`). */
  const isPlatformStaff = computed(() => hasRole(Role.PLATFORM_ADMIN, Role.PLATFORM_MANAGER))

  /** Creating, editing and deleting the organization's own settings. */
  const canManageOrganization = computed(() =>
    hasRole(Role.ORG_ADMIN, Role.PLATFORM_ADMIN, Role.PLATFORM_MANAGER),
  )

  /**
   * Whether requests can be scoped to an organization at all. Without one every
   * organization-scoped endpoint answers 403, so the router shows a screen saying so instead.
   */
  const hasOrganization = computed(() => currentOrganization.value !== null)

  /**
   * A distributor and nothing else. It works in no organization, so of the console it only
   * has its cabinet: every other screen would answer 403.
   */
  const isDistributorOnly = computed(
    () => hasRole(Role.DISTRIBUTOR) && !hasOrganization.value && !isPlatformStaff.value,
  )

  async function load(): Promise<void> {
    loading.value = true
    if (import.meta.env.DEV && import.meta.env.VITE_MOCK === 'true') {
      // Local work against src/mock: a signed-in user with every role, no Keycloak.
      const { MOCK_ME, MOCK_ROLES } = await import('@/mock')
      username.value = MOCK_ME.username
      displayName.value = MOCK_ME.displayName
      email.value = MOCK_ME.email
      roles.value = [...MOCK_ROLES]
      organizations.value = [...MOCK_ME.organizations]
      currentOrganization.value = MOCK_ME.currentOrganization
      loading.value = false
      return
    }
    try {
      const user = await getUser()
      if (!user || user.expired) {
        username.value = null
        displayName.value = null
        email.value = null
        roles.value = []
        organizations.value = []
        currentOrganization.value = null
        return
      }
      const profile = user.profile as Record<string, unknown>
      username.value = (profile.preferred_username as string) ?? user.profile.sub
      displayName.value = (profile.name as string) ?? null
      email.value = (profile.email as string) ?? null
      roles.value = await getRoles()
      organizations.value = await getOrganizations()

      // A remembered choice that the token no longer grants is dropped; with several
      // organizations and no choice, the first is taken so the console opens somewhere.
      const selected = selectedOrganization.value
      if (selected && !organizations.value.includes(selected) && !isPlatformStaff.value) {
        selectOrganization(null)
      }
      if (!selectedOrganization.value && organizations.value.length > 1) {
        selectOrganization(organizations.value[0])
      }
      await loadOrganization()
    } finally {
      loading.value = false
    }
  }

  /**
   * Asks mdm-service which organization this session acts in. The same call keeps
   * the server's copy of the user current, which the audit trail is built from.
   * Never fatal: without an answer the console shows the "no organization" screen.
   */
  async function loadOrganization(): Promise<void> {
    try {
      const me = await meApi.get()
      currentOrganization.value = me.currentOrganization
    } catch {
      currentOrganization.value = null
    }
  }

  /** Switches the organization and reloads the page so no screen keeps the old one's data. */
  function switchOrganization(alias: string): void {
    selectOrganization(alias)
    window.location.assign('/dashboard')
  }

  return {
    username,
    displayName,
    email,
    roles,
    organizations,
    currentOrganization,
    loading,
    authenticated,
    hasAnyPanelRole,
    isPlatformAdmin,
    isPlatformStaff,
    canManageOrganization,
    hasOrganization,
    isDistributorOnly,
    hasRole,
    load,
    login,
    logout,
    switchOrganization,
  }
})
